import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { AdvisorHouseholdsService } from '../../services/advisor-households/advisor-households.service';
import { HouseholdsService } from '../../services/households/households.service';
import { UsersService } from '../../services/users/users.service';
import { OrganizationGroupService } from '../../services/organization-group/organization-group.service';

interface HouseholdItem {
  id: number;
  name: string;
}

@Component({
  selector: 'saffron-household-picker',
  templateUrl: './household-picker.component.html',
  styleUrls: ['./household-picker.component.scss'],
})
export class HouseholdPickerComponent implements OnInit, OnDestroy {
  @Input() disabled = false;
  @Output() householdSelected = new EventEmitter<number | null>();

  expanded = false;
  loadedOnce = false;
  loading = false;
  page = 1;
  readonly pageSize = 10;
  total = 0;
  items: HouseholdItem[] = [];
  selectedHousehold: HouseholdItem | null = null;
  role: string | null = null;
  createOpen = false;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly advisorHouseholdsService: AdvisorHouseholdsService,
    private readonly householdsService: HouseholdsService,
    private readonly usersService: UsersService,
    private readonly organizationGroupService: OrganizationGroupService,
    private readonly elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    void this.usersService.getCurrentUser().then((me) => {
      this.role = me.USER.ROLE;
    });

    // The cached households + selected row belong to the previous firm.
    // Reset so the user starts fresh against the new firm and parents
    // (e.g. upload form) clear their bound household id.
    this.subscriptions.push(
      this.organizationGroupService.firmContextChanged$.subscribe(() => this.onFirmContextChanged()),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private onFirmContextChanged(): void {
    void this.usersService.getCurrentUser().then((me) => {
      this.role = me.USER.ROLE;
    });

    this.expanded = false;
    this.loadedOnce = false;
    this.loading = false;
    this.page = 1;
    this.total = 0;
    this.items = [];

    if (this.selectedHousehold !== null) {
      this.selectedHousehold = null;
      this.householdSelected.emit(null);
    }
  }

  toggle(): void {
    if (this.disabled) return;
    this.expanded = !this.expanded;
    if (this.expanded && !this.loadedOnce) {
      this.loadedOnce = true;
      void this.loadPage();
    }
  }

  async loadPage(): Promise<void> {
    if (this.role === null) return;
    this.loading = true;
    try {
      if (this.role === 'ADVISOR') {
        const offset = (this.page - 1) * this.pageSize;
        const res = await this.advisorHouseholdsService.getMyAssignedHouseholds(this.pageSize, offset);
        this.items = res.data.assignments.map((a) => ({
          id: a.household.HOUSEHOLD_ID,
          name: a.household.HOUSEHOLD_NAME ?? `Household ${a.household.HOUSEHOLD_ID}`,
        }));
        this.total = res.data.total_count;
      } else {
        const res = await this.householdsService.listHouseholds({
          page: this.page,
          pageSize: this.pageSize,
        });
        this.items = res.households.map((h) => ({
          id: h.HOUSEHOLD_ID,
          name: h.HOUSEHOLD_NAME,
        }));
        this.total = res.totalItems ?? res.households.length;
      }
    } catch (err: unknown) {
      console.error('HouseholdPickerComponent: failed to load households', err);
    } finally {
      this.loading = false;
    }
  }

  onPageChange(newPage: number): void {
    this.page = newPage;
    void this.loadPage();
  }

  selectHousehold(item: HouseholdItem): void {
    this.selectedHousehold = item;
    this.householdSelected.emit(item.id);
    this.expanded = false;
  }

  clearSelection(): void {
    this.selectedHousehold = null;
    this.householdSelected.emit(null);
  }

  openCreate(): void {
    this.createOpen = true;
  }

  async onHouseholdCreated(event: { id: number }): Promise<void> {
    // Handle Snowflake PK gotcha fallback (id === 0)
    if (event.id === 0) {
      this.createOpen = false;
      this.page = 1;
      await this.loadPage();
      return;
    }

    this.createOpen = false;
    this.page = 1;
    await this.loadPage();

    const found = this.items.find((item) => item.id === event.id);
    if (found) {
      this.selectHousehold(found);
    } else {
      // Household not in first page — emit the id with a synthesized name
      this.selectedHousehold = { id: event.id, name: 'New household' };
      this.householdSelected.emit(event.id);
    }
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.expanded = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.expanded = false;
  }
}
